import { useEffect, useRef, useState } from 'react';

/**
 * Viewport coordinates for a popover rendered `position: fixed` in a portal.
 *
 * Portal-and-fixed is the only placement that survives this app's layout:
 * `Layout` wraps the page body in `overflow-hidden` plus an `overflow-auto`
 * `main`, which clips any absolutely-positioned panel that crosses their edge.
 */
export type PopoverPlacement = { top: number; left: number };

export type PopoverOptions = {
  width: number;
  /** Fallback until the panel has rendered and can be measured. */
  minHeight: number;
  /**
   * Which edge lines up with the trigger. A panel wider than its button reads
   * better right-aligned; one roughly as wide as the button, left-aligned.
   */
  align: 'left' | 'right';
  margin?: number;
};

const DEFAULT_MARGIN = 8;

export function popoverPlacement(
  trigger: DOMRect,
  height: number,
  opts: PopoverOptions
): PopoverPlacement {
  const margin = opts.margin ?? DEFAULT_MARGIN;
  const spaceBelow = window.innerHeight - trigger.bottom - margin;
  const spaceAbove = trigger.top - margin;

  // Stays below unless above is genuinely roomier. Flipping the moment below
  // falls short sends a tall panel upward on most viewports — filter rows sit
  // mid-page — where it covers the header and its own trigger.
  let top: number;
  if (spaceBelow >= height) {
    top = trigger.bottom + 4;
  } else if (spaceAbove >= height) {
    top = trigger.top - height - 4;
  } else {
    // Neither side fits: pinned to the viewport, so the top edge stays visible
    // even though the panel is cut off.
    top = Math.max(
      margin,
      Math.min(trigger.bottom + 4, window.innerHeight - height - margin)
    );
  }

  // Right edge clamped first, left margin last. The other order returns a
  // negative bound once the viewport is narrower than the panel.
  const preferred =
    opts.align === 'left' ? trigger.left : trigger.right - opts.width;
  const maxLeft = window.innerWidth - opts.width - margin;
  const left = Math.max(margin, Math.min(preferred, maxLeft));

  return { top, left };
}

/**
 * Keeps a portaled popover pinned to its trigger while open.
 *
 * Re-measures on resize and on scroll from any ancestor (`capture: true` —
 * the page body scrolls inside `main`, not the window), coalesced into one
 * animation frame because scrolling fires dozens of events a second and each
 * one reads layout and re-renders.
 */
export function usePopoverPosition<T extends HTMLElement>(
  triggerRef: React.RefObject<T | null>,
  isOpen: boolean,
  opts: PopoverOptions
) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<PopoverPlacement>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    if (!isOpen) return;

    let frame = 0;
    const place = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      if (!trigger) return;
      // Measured once rendered; minHeight is only the opening guess, and a
      // six-row month makes the real panel taller.
      const height = panelRef.current?.offsetHeight || opts.minHeight;
      setPlacement(popoverPlacement(trigger, height, opts));
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };

    place();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, triggerRef, opts.width, opts.minHeight, opts.align, opts.margin]);

  return { panelRef, placement };
}
